import React from "react";
import { ContentAlignment, Icon } from "fr-ito-web-react/react/esm";
import { Caption, Tile, Image } from "@common/DynamicUikit";
import Toggle from "@common/Toggle";
import styles from "./styles.module.css";

interface UserInfoProps {
  /**
   * @en Approved date of style
   */
  approvedDate?: string;
  /**
   * @en Unit for height
   */
  heightUnit?: string;
  /**
   * @en Height of the model
   */
  modelHeight?: string;
  /**
   * @en User image url
   */
  profileImage?: React.ReactElement;
  /**
   * @en Store information of user
   */
  storeInfo?: string;
  /**
   * @en Style user name
   */
  userName: string;
  /**
   * @en User review for style
   */
  userReview?: string;
  /**
   * @en To determine whether the badge should be rendered with the user name
   */
  isBadge: boolean;
  /**
   * @en To transition to page of the store page
   */
  storeLink?: string;
  /**
   * @en transit to the personal page
   */
  personalPageLink?: string;
  /**
   * userNameGaAction for Google Analytics
   */
  userNameGaAction?: string;
  /**
   * storeNameGaAction for Google Analytics
   */
  storeNameGaAction?: string;
  /**
   * Callback function for Google Analytics
   */
  gaCallback?: (e: HTMLElement, type?: string, gaAction?: string) => void;
  /**
   * Category for Google Analytics
   */
  gaCategory?: string;
  /**
   * userNameGaLabel for Google Analytics
   */
  userNameGaLabel?: string;
  /**
   * storeNameGaLabel for Google Analytics
   */
  storeNameGaLabel?: string;
  /**
   * Value for Google Analytics
   */
  gaValue?: number | null;
  /**
   * Value for wish count
   */
  count?: string | undefined;
  /**
   * To determine whether the wish button is displayed
   */
  showWish: boolean;
  /**
   * To determine whether the wish button is toggled
   */
  toggled: boolean;
  /**
   * Callback function for wish button click
   */
  handleWishClick: (e: any) => void;
  /**
   * To determine whether the only show wish button
   */
  wishOnly?: boolean;
}

const UserInfo = (props: UserInfoProps) => {
  const {
    approvedDate,
    heightUnit,
    modelHeight,
    profileImage,
    storeInfo,
    userName,
    userReview,
    isBadge,
    storeLink,
    personalPageLink,
    userNameGaAction,
    storeNameGaAction,
    gaCallback,
    gaCategory,
    userNameGaLabel,
    storeNameGaLabel,
    gaValue,
    count,
    showWish,
    toggled,
    handleWishClick,
    wishOnly = false
  } = props;

  const instaImage = (
    <Image
      alt="Instagram image"
      ratio="1x1"
      src="https://asset.uniqlo.com/g/pictures/instagram-gradient.png"
      noImage={{ imageSize: "smallest", imageType: "user" }}
    />
  );

  const image = profileImage || instaImage;

  const profileImageContainer = (
    <Tile
      className={styles.StyleUserInfoLink}
      gaLabel={userNameGaLabel}
      gaCategory={gaCategory}
      gaAction={userNameGaAction}
      gaValue={gaValue}
      gaCallback={gaCallback}
      href={personalPageLink}
      target="_self"
    >
      <div
        className={
          profileImage
            ? `${styles.StyleUserInfoImage}`
            : `${styles.StyleUserInfoImage} ${styles.UserInfoInstaImage}`
        }
      >
        {image}
      </div>
    </Tile>
  );

  const profileNameSection = (
    <ContentAlignment
      element="div"
      direction="row"
      alignment="center"
      className={styles.StyleUserInfoName}
    >
      <Tile
        className={styles.StyleUserInfoLink}
        gaLabel={userNameGaLabel}
        gaCategory={gaCategory}
        gaAction={userNameGaAction}
        gaValue={gaValue}
        gaCallback={gaCallback}
        href={personalPageLink}
        target="_self"
      >
        <Caption
          variant="small"
          element="span"
          className={styles.StyleUserInfoProfileName}
        >
          {userName}
        </Caption>
      </Tile>
      {isBadge && (
        <Icon
          className={styles.StyleUserInfoIcon}
          name="verifiedBadge"
          size={12}
          fill
        />
      )}
      {modelHeight && (
        <Tile
          className={`${styles.StyleUserInfoLink} ${styles.StyleUserInfoSizeWrapper}`}
          gaLabel={userNameGaLabel}
          gaCategory={gaCategory}
          gaAction={userNameGaAction}
          gaValue={gaValue}
          gaCallback={gaCallback}
          href={personalPageLink}
          target="_self"
        >
          <Caption
            element="p"
            variant="small"
            className={`${styles.StyleUserInfoSize} ${
              modelHeight === "-" ? styles.StyleUserInfoCarouselSizeWidth : null
            }`}
          >
            {`${modelHeight || "-"}${heightUnit || ""}`}
          </Caption>
        </Tile>
      )}
    </ContentAlignment>
  );

  const imageSection = (
    <ContentAlignment
      element="section"
      direction="row"
      justifyContent={wishOnly ? "flex-end" : "flex-start"}
      className={styles.StyleUserInfoContainer}
    >
      {!wishOnly && (
        <>
          {profileImageContainer}
          <section
            className={`${styles.StyleUserInfoSection} ${
              showWish
                ? styles.StyleUserInfoSectionWithWish
                : styles.StyleUserInfoSectionWithoutWish
            }`}
          >
            {profileNameSection}
            {storeInfo &&
              (storeLink ? (
                <Tile
                  className={styles.StyleUserInfoLink}
                  gaLabel={storeNameGaLabel}
                  gaCategory={gaCategory}
                  gaAction={storeNameGaAction}
                  gaValue={gaValue}
                  gaCallback={gaCallback}
                  href={storeLink}
                  target="_self"
                >
                  <Caption
                    element="p"
                    variant="small"
                    className={styles.StyleUserInfoStoreName}
                  >
                    {storeInfo}
                  </Caption>
                </Tile>
              ) : (
                <div className={styles.StyleUserInfoLink}>
                  <Caption
                    element="p"
                    variant="small"
                    className={styles.StyleUserInfoStoreName}
                  >
                    {storeInfo}
                  </Caption>
                </div>
              ))}
          </section>
        </>
      )}
      {showWish && (
        <section className={styles.WishToggle}>
          <Toggle
            variant="icon"
            icon="heart"
            count={count}
            toggled={toggled}
            onClick={handleWishClick}
          />
        </section>
      )}
    </ContentAlignment>
  );

  return (
    <ContentAlignment direction="column" className={styles.StyleUserInfo}>
      {imageSection}
      {userReview && (
        <Caption
          element="p"
          variant="standard"
          className={styles.StyleUserInfoReview}
        >
          {userReview}
        </Caption>
      )}
      {approvedDate && (
        <Caption
          element="p"
          variant="small"
          className={styles.StyleUserInfoDate}
        >
          {approvedDate}
        </Caption>
      )}
    </ContentAlignment>
  );
};

export default UserInfo;
